import {
  ValidationRules,
  ValidationResult,
  ValidationError,
  UploadedFileData,
  FileUploadStatus,
} from './DocumentUploadManager.props';

export const ACCEPTED_MIME_TYPES = {
  'text/plain': ['.txt'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
    '.pptx',
  ],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/tiff': ['.tiff', '.tif'],
} as const;

// Unified validation rules matching DocumentUploadManager
export const DEFAULT_VALIDATION_RULES: Required<ValidationRules> = {
  maxFiles: 15,
  maxFileSize: 50 * 1024 * 1024, // 50MB
  maxBatchSize: 500 * 1024 * 1024, // 500MB
  acceptedExtensions: [
    '.pdf',
    '.doc',
    '.docx',
    '.jpg',
    '.jpeg',
    '.png',
    '.tiff',
    '.tif',
    '.xlsx',
    '.xls',
    '.ppt',
    '.pptx',
    '.txt',
  ],
};

const generateFileId = (): string => {
  return `file_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > -1 ? fileName.slice(lastDot).toLowerCase() : '';
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const isValidFileType = (file: File, acceptedExtensions: string[]): boolean => {
  const extension = getFileExtension(file.name);
  if (!acceptedExtensions.includes(extension)) return false;

  // MIME type validation is handled by extension check
  // Additional MIME validation can be added here if needed

  return true;
};

export const validateFiles = (
  files: File[],
  existingFiles: UploadedFileData[],
  rules: ValidationRules = {}
): ValidationResult => {
  const mergedRules = { ...DEFAULT_VALIDATION_RULES, ...rules };
  const errors: ValidationError[] = [];

  // Step 1: Check individual file validations (type, size, empty)
  const validFiles: File[] = [];
  const oversizedFiles: File[] = [];

  files.forEach((file) => {
    const fileId = generateFileId();

    // Check if file is empty
    if (file.size === 0) {
      errors.push({
        fileId,
        fileName: file.name,
        errorType: 'failed',
        message: 'File is empty and cannot be uploaded',
      });
      return;
    }

    // Check file type
    if (!isValidFileType(file, mergedRules.acceptedExtensions)) {
      const extension = getFileExtension(file.name);
      errors.push({
        fileId,
        fileName: file.name,
        errorType: 'type-not-allowed',
        message: `File type "${extension}" not allowed. Supported: ${Object.values(ACCEPTED_MIME_TYPES).flat().join(', ')}`,
      });
      return;
    }

    // Check individual file size (files > 50MB are excluded from batch size calculation)
    if (file.size > mergedRules.maxFileSize) {
      errors.push({
        fileId,
        fileName: file.name,
        errorType: 'size-too-large',
        message: `File size ${formatFileSize(file.size)} exceeds ${formatFileSize(mergedRules.maxFileSize)} limit`,
      });
      oversizedFiles.push(file);
      return;
    }

    validFiles.push(file);
  });

  // Step 2: Handle file count validation
  // Show all files, but mark excess files (16th, 17th, etc.) with file-count-exceeded
  const allowedNewFiles = Math.max(
    0,
    mergedRules.maxFiles - existingFiles.length
  );

  if (files.length > allowedNewFiles) {
    // Mark files beyond the 15th position as file-count-exceeded
    files.slice(allowedNewFiles).forEach((file) => {
      // Only add error if it's not already marked for other reasons
      const hasOtherError = errors.some(
        (error) => error.fileName === file.name
      );
      if (!hasOtherError) {
        errors.push({
          fileId: generateFileId(),
          fileName: file.name,
          errorType: 'file-count-exceeded',
          message: `Maximum ${mergedRules.maxFiles} files allowed. This file exceeds the limit.`,
        });
      }
    });
  }

  // Step 3: Calculate batch size for valid files only (excluding oversized files)
  const existingValidSize = existingFiles
    .filter((f) => f.file.size <= mergedRules.maxFileSize)
    .reduce((sum, f) => sum + f.file.size, 0);

  let currentBatchSize = existingValidSize;
  let batchSizeExceededIndex = -1;

  // Check each valid file in order to find where batch size is exceeded
  for (let i = 0; i < validFiles.length && i < allowedNewFiles; i++) {
    const file = validFiles[i];
    if (currentBatchSize + file.size > mergedRules.maxBatchSize) {
      batchSizeExceededIndex = i;
      break;
    }
    currentBatchSize += file.size;
  }

  // Mark files that exceed batch size (from the nth file onwards)
  if (batchSizeExceededIndex >= 0) {
    for (
      let i = batchSizeExceededIndex;
      i < validFiles.length && i < allowedNewFiles;
      i++
    ) {
      const file = validFiles[i];
      const hasOtherError = errors.some(
        (error) => error.fileName === file.name
      );
      if (!hasOtherError) {
        errors.push({
          fileId: generateFileId(),
          fileName: file.name,
          errorType: 'batch-size-exceeded',
          message: `Adding this file would exceed the ${formatFileSize(mergedRules.maxBatchSize)} batch limit`,
        });
      }
    }
  }

  return { valid: errors.length === 0, errors };
};
